import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { openCookieSettings } from '../components/CookieConsent';

/* ─── scroll reveal ─── */
const Reveal = ({ children, delay = 0, style = {} }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); observer.disconnect(); }
    }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ ...style, opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s` }}>
      {children}
    </div>
  );
};

/* ─── FAQ ─── */
const FAQItem = ({ q, a, borderColor, textColor, mutedColor }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${borderColor}`, padding: '20px 0', cursor: 'pointer' }} onClick={() => setOpen(!open)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 17, fontWeight: 600, color: textColor, fontFamily: 'Plus Jakarta Sans, sans-serif', paddingRight: 16 }}>{q}</span>
        <span style={{ fontSize: 22, color: '#00d4aa', fontWeight: 300, transition: 'transform 0.3s', transform: open ? 'rotate(45deg)' : 'none', flexShrink: 0 }}>+</span>
      </div>
      <div style={{ maxHeight: open ? 300 : 0, overflow: 'hidden', transition: 'max-height 0.4s ease' }}>
        <p style={{ marginTop: 12, fontSize: 15, lineHeight: 1.7, color: mutedColor, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{a}</p>
      </div>
    </div>
  );
};

const PricingPage = () => {
  const { isDark, toggleTheme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [annual, setAnnual] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const API_BASE = process.env.REACT_APP_API_URL || '/api';

  const handleCheckout = async (planName, billing) => {
    // Require sign-in before checkout
    if (!user) {
      const billingStr = billing ? 'annual' : 'monthly';
      window.location.href = `/register?plan=${planName.toLowerCase()}&billing=${billingStr}`;
      return;
    }
    setCheckoutLoading(planName);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ planName: planName.toLowerCase(), billing: billing ? 'annual' : 'monthly' })
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message && data.message.includes('not configured')) {
          window.location.href = '/register';
          return;
        }
        if (data.manageBilling) {
          alert(data.message);
          window.location.href = '/settings?tab=subscription';
          return;
        }
        alert(data.message || 'Failed to start checkout');
        return;
      }
      if (data.url) window.location.href = data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      window.location.href = '/register';
    } finally {
      setCheckoutLoading(null);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const c = {
    pageBg: isDark ? '#020617' : '#ffffff',
    pageBg2: isDark ? '#0a0f1e' : '#f8fafc',
    cardBg: isDark ? '#0f172a' : '#ffffff',
    cardBorder: isDark ? '#1e293b' : '#e2e8f0',
    textPrimary: isDark ? '#f1f5f9' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    textMuted: isDark ? '#64748b' : '#94a3b8',
    navBg: isDark ? 'rgba(2,6,23,0.92)' : 'rgba(255,255,255,0.95)',
    navBorder: isDark ? 'rgba(30,41,59,0.6)' : 'rgba(226,232,240,0.8)',
    navLink: isDark ? '#94a3b8' : '#475569',
    navShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)',
    inputBg: isDark ? '#0f172a' : '#ffffff',
    inputBorder: isDark ? '#1e293b' : '#d1d5db',
    inputText: isDark ? '#f1f5f9' : '#0f172a',
    highlightBg: isDark ? 'rgba(0,212,170,0.06)' : 'rgba(0,212,170,0.03)',
  };

  const tiers = [
    {
      name: 'Standard',
      desc: 'For county and city health departments getting started with modern surveillance.',
      price: annual ? 399 : 499,
      period: annual ? '/mo billed annually' : '/mo',
      annualTotal: annual ? '$4,788/yr' : '$5,988/yr',
      color: '#3b82f6',
      badge: null,
      users: 'Up to 25 users',
      workspaces: '1 workspace',
      cta: 'Get Started',
      features: [
        'All 15 surveillance modules',
        'Up to 25 users',
        '1 workspace (jurisdiction)',
        'CSV bulk import & export',
        'All report types (PDF, Excel, CSV)',
        'Dashboard analytics & charts',
        'Email notifications & alerts',
        'Dark/light mode interface',
        'Standard support (48hr response)',
        '99% uptime SLA',
      ],
    },
    {
      name: 'Professional',
      desc: 'For state health departments and larger agencies needing AI-powered analytics.',
      price: annual ? 799 : 999,
      period: annual ? '/mo billed annually' : '/mo',
      annualTotal: annual ? '$9,588/yr' : '$11,988/yr',
      color: '#00d4aa',
      badge: 'Most Popular',
      users: 'Up to 100 users',
      workspaces: 'Up to 3 workspaces',
      cta: 'Get Started',
      features: [
        'Everything in Standard, plus:',
        'Up to 100 users',
        'Up to 3 workspaces',
        'AI Insights & natural language queries',
        'AI anomaly detection & alerts',
        'AI-generated epidemiological reports',
        'Health equity disparity analytics',
        'Priority support (24hr response)',
        'Custom branding on reports',
        'HIPAA BAA included',
        '99.5% uptime SLA',
      ],
    },
    {
      name: 'Enterprise',
      desc: 'For national agencies and organizations managing multiple jurisdictions.',
      price: annual ? 1999 : 2499,
      period: annual ? '/mo billed annually' : '/mo',
      annualTotal: annual ? '$23,988/yr' : '$29,988/yr',
      color: '#8b5cf6',
      badge: null,
      users: 'Up to 500 users',
      workspaces: 'Up to 10 workspaces',
      cta: 'Get Started',
      features: [
        'Everything in Professional, plus:',
        'Up to 500 users',
        'Up to 10 workspaces',
        'Advanced AI: predictive modeling',
        'API access for EHR/LIMS integration',
        'Dedicated account manager',
        'Phone + email support (4hr response)',
        'Custom onboarding & training (8hrs)',
        'Quarterly business reviews',
        'Data migration assistance',
        '99.9% uptime SLA',
      ],
    },
  ];

  const addons = [
    { item: 'Additional Users', price: '$15/user/mo', desc: 'Beyond your plan limit' },
    { item: 'Additional Workspace', price: '$299/workspace/mo', desc: 'Extra jurisdictions' },
    { item: 'Custom Integration', price: 'From $5,000', desc: 'EHR, LIMS, HL7 FHIR connectors' },
    { item: 'Training Session', price: '$500/session', desc: '2-hour hands-on training' },
    { item: 'Premium Support', price: '$299/mo', desc: '4hr response, dedicated contact' },
    { item: 'Data Migration', price: 'From $2,000', desc: 'Legacy system migration service' },
  ];

  const faqs = [
    { q: 'Can I try EpiSurveillance before subscribing?', a: 'Yes! We offer free Viewer demo accounts so you can explore the full platform with sample data. Register at episurveillance.com/register and request a demo account — no credit card required.' },
    { q: 'What payment methods do you accept?', a: 'We accept all major credit/debit cards, ACH bank transfers, and wire transfers via Stripe. For government agencies, we also accept Purchase Orders (POs) with NET-30 and NET-60 payment terms.' },
    { q: 'Can we switch plans or cancel anytime?', a: 'Yes — you can cancel your subscription at any time from your account settings. Cancellations take effect at the end of the current billing period. Please note: we do not offer refunds for any partial periods, unused time, or previous payments. No exceptions.' },
    { q: 'What is your refund policy?', a: 'EpiSurveillance operates a strict no-refund policy. All subscription payments are non-refundable. You may cancel anytime and your access continues until the end of the paid period. We recommend starting with a free Viewer account to evaluate the platform before subscribing.' },
    { q: 'How does annual billing work?', a: 'Annual billing saves you 20% compared to monthly pricing. You\'re billed once per year upfront. All annual plans include the same features as monthly plans at a reduced rate.' },
    { q: 'What happens if we exceed our user limit?', a: 'We\'ll notify you at 80% capacity. You can add individual users at $15/user/month or upgrade to a higher tier for better value. Existing users are never locked out.' },
    { q: 'Is there a government/education discount?', a: 'Yes. We offer special pricing for government agencies purchasing through official procurement channels, LMIC (Low and Middle Income Countries), and academic institutions. Contact us for details.' },
    { q: 'Is EpiSurveillance HIPAA compliant?', a: 'EpiSurveillance is built to support HIPAA compliance, with a Business Associate Agreement (BAA) available on Professional and above.' },
    { q: 'Can you build a custom surveillance system for us?', a: 'Yes. We build custom surveillance modules to your requirements (your diseases, data fields, workflows and reports), typically within 2 weeks.' },
    { q: 'Do you offer multi-year contracts?', a: 'Yes — multi-year commitments (2-3 years) receive additional discounts of 10-15% beyond the annual pricing. Ideal for government agencies with multi-year budget cycles.' },
  ];

  const navLinks = ['Features', 'Pricing', 'Contact'];

  return (
    <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', background: c.pageBg, color: c.textPrimary, minHeight: '100vh' }}>

      <style>{`
        .p-nav-link { color: ${c.navLink}; cursor: pointer; font-weight: 500; font-size: 15px; transition: color 0.2s; text-decoration: none; }
        .p-nav-link:hover { color: #00d4aa; }
        .p-cta-primary { background: linear-gradient(135deg, #00d4aa, #00b894); color: #020617; border: none; padding: 12px 28px; border-radius: 10px; font-weight: 700; font-size: 15px; cursor: pointer; transition: transform 0.2s, box-shadow 0.2s; text-decoration: none; display: inline-block; }
        .p-cta-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 25px rgba(0,212,170,0.3); }
        .p-cta-secondary { background: transparent; color: ${c.textPrimary}; border: 2px solid ${c.cardBorder}; padding: 12px 28px; border-radius: 10px; font-weight: 600; font-size: 15px; cursor: pointer; transition: all 0.2s; text-decoration: none; display: inline-block; }
        .p-cta-secondary:hover { border-color: #00d4aa; color: #00d4aa; }
        .p-card { background: ${c.cardBg}; border: 1px solid ${c.cardBorder}; border-radius: 20px; padding: 36px; transition: transform 0.3s, box-shadow 0.3s; }
        .p-card:hover { transform: translateY(-4px); box-shadow: 0 20px 40px rgba(0,0,0,${isDark ? '0.3' : '0.08'}); }
        .p-card-pop { background: ${c.cardBg}; border: 2px solid #00d4aa; border-radius: 20px; padding: 36px; position: relative; transition: transform 0.3s, box-shadow 0.3s; }
        .p-card-pop:hover { transform: translateY(-6px); box-shadow: 0 20px 50px rgba(0,212,170,${isDark ? '0.2' : '0.12'}); }
        @media (max-width: 900px) {
          .p-grid-3 { grid-template-columns: 1fr !important; }
          .p-grid-2 { grid-template-columns: 1fr !important; }
          .p-hero-title { font-size: 36px !important; }
          .p-nav-desktop { display: none !important; }
          .p-hamburger { display: flex !important; }
        }
        @media (min-width: 901px) {
          .p-hamburger { display: none !important; }
          .p-mobile-nav { display: none !important; }
        }
      `}</style>

      {/* ─── NAVBAR ─── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        background: scrolled ? c.navBg : 'transparent',
        borderBottom: scrolled ? `1px solid ${c.navBorder}` : 'none',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        boxShadow: scrolled ? c.navShadow : 'none',
        transition: 'all 0.3s ease',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #00d4aa, #00b894)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14, color: '#020617' }}>ES</div>
            <span style={{ fontSize: 18, fontWeight: 800, color: c.textPrimary, fontFamily: 'DM Serif Display, serif' }}>EpiSurveillance</span>
          </Link>
          <div className="p-nav-desktop" style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
            <Link to="/#features" className="p-nav-link">Features</Link>
            <Link to="/pricing" className="p-nav-link" style={{ color: '#00d4aa', fontWeight: 700 }}>Pricing</Link>
            <Link to="/#contact" className="p-nav-link">Contact</Link>
            <button onClick={toggleTheme} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }}>{isDark ? '☀️' : '🌙'}</button>
            {user ? (
              <Link to="/dashboard" className="p-cta-primary" style={{ padding: '8px 20px', fontSize: 14 }}>Dashboard →</Link>
            ) : (<>
              <Link to="/login" className="p-cta-secondary" style={{ padding: '8px 20px', fontSize: 14 }}>Sign In</Link>
              <Link to="/register" className="p-cta-primary" style={{ padding: '8px 20px', fontSize: 14 }}>Get Started</Link>
            </>)}
          </div>
          <button className="p-hamburger" onClick={() => setMobileNav(!mobileNav)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 5, padding: 8 }}>
            <span style={{ width: 24, height: 2, background: c.textPrimary, borderRadius: 2, transition: '0.3s', transform: mobileNav ? 'rotate(45deg) translate(5px, 5px)' : 'none' }} />
            <span style={{ width: 24, height: 2, background: c.textPrimary, borderRadius: 2, transition: '0.3s', opacity: mobileNav ? 0 : 1 }} />
            <span style={{ width: 24, height: 2, background: c.textPrimary, borderRadius: 2, transition: '0.3s', transform: mobileNav ? 'rotate(-45deg) translate(5px, -5px)' : 'none' }} />
          </button>
        </div>
        {mobileNav && (
          <div className="p-mobile-nav" style={{ background: c.navBg, borderTop: `1px solid ${c.navBorder}`, padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Link to="/" className="p-nav-link" onClick={() => setMobileNav(false)}>Home</Link>
            <Link to="/pricing" className="p-nav-link" style={{ color: '#00d4aa' }} onClick={() => setMobileNav(false)}>Pricing</Link>
            <Link to="/login" className="p-nav-link" onClick={() => setMobileNav(false)}>Sign In</Link>
            <Link to="/register" className="p-cta-primary" style={{ textAlign: 'center' }} onClick={() => setMobileNav(false)}>Get Started</Link>
          </div>
        )}
      </nav>

      {/* ─── HERO ─── */}
      <section style={{ paddingTop: 140, paddingBottom: 60, textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -200, left: '50%', transform: 'translateX(-50%)', width: 800, height: 800, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,212,170,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '0 24px', position: 'relative' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 20px', borderRadius: 40, background: c.highlightBg, border: `1px solid ${c.cardBorder}`, marginBottom: 24, fontSize: 14, color: c.textSecondary }}>
            💰 Simple, transparent pricing — no hidden fees
          </div>
          <h1 className="p-hero-title" style={{ fontSize: 52, fontFamily: 'DM Serif Display, serif', fontWeight: 400, lineHeight: 1.15, marginBottom: 20, color: c.textPrimary }}>
            Choose the plan that<br />
            <span style={{ color: '#00d4aa' }}>protects your community</span>
          </h1>
          <p style={{ fontSize: 18, color: c.textSecondary, lineHeight: 1.7, maxWidth: 560, margin: '0 auto 32px' }}>
            Modern disease surveillance for every health department. Start with what you need, scale as you grow.
          </p>

          {/* Billing Toggle */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 16, padding: '6px 8px', borderRadius: 14, background: c.highlightBg, border: `1px solid ${c.cardBorder}` }}>
            <button onClick={() => setAnnual(false)} style={{
              padding: '10px 24px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 600, transition: 'all 0.3s',
              background: !annual ? '#00d4aa' : 'transparent', color: !annual ? '#020617' : c.textSecondary,
            }}>Monthly</button>
            <button onClick={() => setAnnual(true)} style={{
              padding: '10px 24px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 600, transition: 'all 0.3s',
              background: annual ? '#00d4aa' : 'transparent', color: annual ? '#020617' : c.textSecondary,
            }}>
              Annual <span style={{ fontSize: 12, fontWeight: 800, marginLeft: 6, background: annual ? 'rgba(2,6,23,0.15)' : 'rgba(0,212,170,0.15)', padding: '2px 8px', borderRadius: 6 }}>Save 20%</span>
            </button>
          </div>
        </div>
      </section>

      {/* ─── PRICING CARDS ─── */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px 80px' }}>
        <div className="p-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, alignItems: 'start' }}>
          {tiers.map((tier, i) => (
            <Reveal key={tier.name} delay={i * 0.1}>
              <div className={tier.badge ? 'p-card-pop' : 'p-card'} style={{ height: '100%' }}>
                {tier.badge && (
                  <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #00d4aa, #00b894)', color: '#020617', padding: '6px 20px', borderRadius: 20, fontSize: 13, fontWeight: 800, letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
                    ★ {tier.badge.toUpperCase()}
                  </div>
                )}
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ fontSize: 22, fontWeight: 700, fontFamily: 'DM Serif Display, serif', marginBottom: 8, color: c.textPrimary }}>{tier.name}</h3>
                  <p style={{ fontSize: 14, color: c.textSecondary, lineHeight: 1.6 }}>{tier.desc}</p>
                </div>

                <div style={{ marginBottom: 24 }}>
                  <span style={{ fontSize: 48, fontWeight: 800, color: tier.color }}>${tier.price.toLocaleString()}</span>
                  <span style={{ fontSize: 15, color: c.textMuted, marginLeft: 4 }}>{tier.period}</span>
                  <div style={{ fontSize: 13, color: c.textMuted, marginTop: 4 }}>
                    {tier.annualTotal} total · {tier.users} · {tier.workspaces}
                  </div>
                </div>

                <button
                  onClick={() => handleCheckout(tier.name, annual)}
                  disabled={checkoutLoading === tier.name}
                  className={checkoutLoading === tier.name ? '' : 'p-cta-primary'}
                  style={{
                    display: 'block', width: '100%', textAlign: 'center', marginBottom: 28, fontSize: 16,
                    ...(checkoutLoading === tier.name
                      ? { background: '#64748b', color: '#94a3b8', border: 'none', padding: '12px 28px', borderRadius: 10, fontWeight: 700, cursor: 'not-allowed', opacity: 0.7 }
                      : {
                          background: tier.badge ? 'linear-gradient(135deg, #00d4aa, #00b894)' : c.cardBg,
                          color: tier.badge ? '#020617' : '#00d4aa',
                          border: tier.badge ? 'none' : '2px solid #00d4aa',
                        }
                    ),
                  }}
                >
                  {checkoutLoading === tier.name ? 'Processing...' : tier.cta}
                </button>

                <div>
                  {tier.features.map((f, j) => (
                    <div key={j} style={{ display: 'flex', gap: 10, padding: '6px 0', fontSize: 14 }}>
                      <span style={{ color: '#00d4aa', flexShrink: 0 }}>✓</span>
                      <span style={{ color: j === 0 && i > 0 ? '#00d4aa' : c.textSecondary, fontWeight: j === 0 && i > 0 ? 600 : 400 }}>{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Custom build offer (applies to every plan) */}
        <Reveal delay={0.15}>
          <div style={{ marginTop: 32, padding: '20px 28px', borderRadius: 16, background: isDark ? 'rgba(0,212,170,0.08)' : '#ecfdf8', border: '1px solid rgba(0,212,170,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 260 }}>
              <span style={{ fontSize: 28 }}>⚡</span>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: c.textPrimary }}>Need a custom surveillance system?</div>
                <div style={{ fontSize: 14, color: c.textSecondary, marginTop: 2 }}>We build it to your requirements (your diseases, data fields, workflows and reports) in about 2 weeks, on any plan.</div>
              </div>
            </div>
            <a href="mailto:support@episurveillance.com?subject=Custom%20surveillance%20build" className="p-cta-primary" style={{ padding: '12px 28px', fontSize: 15 }}>Tell us what you need</a>
          </div>
        </Reveal>

        {/* Government / Custom */}
        <Reveal delay={0.2}>
          <div style={{ marginTop: 32, padding: '40px 48px', borderRadius: 20, background: isDark ? 'linear-gradient(135deg, #0f172a, #1a1a2e)' : 'linear-gradient(135deg, #f8fafc, #f0f4ff)', border: `2px solid ${isDark ? '#1e293b' : '#e2e8f0'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 32 }}>
            <div style={{ flex: 1, minWidth: 280 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <span style={{ fontSize: 32 }}>🏛</span>
                <h3 style={{ fontSize: 26, fontFamily: 'DM Serif Display, serif', color: c.textPrimary, margin: 0 }}>Government & Custom</h3>
              </div>
              <p style={{ fontSize: 15, color: c.textSecondary, lineHeight: 1.7, marginBottom: 16 }}>
                For federal agencies, international ministries of health, multi-state compacts, and organizations requiring dedicated infrastructure, custom integrations, or procurement-compliant contracts.
              </p>
              <div className="p-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {['Unlimited users & workspaces', 'Dedicated infrastructure option', 'HL7 FHIR / ELR integration', '24/7 priority support with SLA', 'Custom development & features', 'On-site training & implementation', 'Custom surveillance built in ~2 weeks', 'Multi-year contract discounts', 'Purchase order & NET-60 terms', 'Data migration from legacy systems'].map(f => (
                  <div key={f} style={{ display: 'flex', gap: 8, fontSize: 14, color: c.textSecondary, padding: '3px 0' }}>
                    <span style={{ color: '#00d4aa' }}>✓</span> {f}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ textAlign: 'center', minWidth: 220 }}>
              <div style={{ fontSize: 36, fontWeight: 800, color: c.textPrimary, marginBottom: 8, fontFamily: 'DM Serif Display, serif' }}>Custom Pricing</div>
              <p style={{ fontSize: 14, color: c.textMuted, marginBottom: 20 }}>Tailored to your agency's needs</p>
              <a href="mailto:support@episurveillance.com?subject=EpiSurveillance%20Government%20Plan%20Inquiry" className="p-cta-primary" style={{ padding: '14px 36px', fontSize: 16 }}>Contact Sales</a>
              <div style={{ marginTop: 12 }}>
                <a href="tel:5403227406" style={{ fontSize: 14, color: '#00d4aa', textDecoration: 'none' }}>📞 (540) 322-7406</a>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ─── ADD-ONS ─── */}
      <section style={{ background: c.pageBg2, padding: '80px 24px' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <h2 style={{ fontSize: 36, fontFamily: 'DM Serif Display, serif', marginBottom: 12, color: c.textPrimary }}>Add-Ons & Services</h2>
              <p style={{ fontSize: 16, color: c.textSecondary }}>Extend your plan with additional capabilities as needed</p>
            </div>
          </Reveal>
          <div className="p-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {addons.map((a, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div style={{ background: c.cardBg, border: `1px solid ${c.cardBorder}`, borderRadius: 14, padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: c.textPrimary }}>{a.item}</div>
                    <div style={{ fontSize: 13, color: c.textMuted }}>{a.desc}</div>
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#00d4aa', whiteSpace: 'nowrap' }}>{a.price}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── COMPARISON TABLE ─── */}
      <section style={{ maxWidth: 1000, margin: '0 auto', padding: '80px 24px' }}>
        <Reveal>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 36, fontFamily: 'DM Serif Display, serif', marginBottom: 12, color: c.textPrimary }}>Compare Plans</h2>
            <p style={{ fontSize: 16, color: c.textSecondary }}>Full feature comparison across all tiers</p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '14px 16px', borderBottom: `2px solid ${c.cardBorder}`, color: c.textMuted, fontWeight: 600 }}>Feature</th>
                  {['Standard', 'Professional', 'Enterprise', 'Government'].map(t => (
                    <th key={t} style={{ textAlign: 'center', padding: '14px 16px', borderBottom: `2px solid ${c.cardBorder}`, color: t === 'Professional' ? '#00d4aa' : c.textPrimary, fontWeight: 700 }}>{t}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['Users', '25', '100', '500', 'Unlimited'],
                  ['Workspaces', '1', '3', '10', 'Unlimited'],
                  ['All 15 Modules', '✓', '✓', '✓', '✓'],
                  ['Dashboard & Charts', '✓', '✓', '✓', '✓'],
                  ['CSV Import/Export', '✓', '✓', '✓', '✓'],
                  ['Report Generation', '✓', '✓', '✓', '✓'],
                  ['AI Insights & NLP Queries', '—', '✓', '✓', '✓'],
                  ['AI Anomaly Detection', '—', '✓', '✓', '✓'],
                  ['AI Predictive Modeling', '—', '—', '✓', '✓'],
                  ['Health Equity Analytics', 'Basic', 'Full', 'Full', 'Full'],
                  ['API Access', '—', '—', '✓', '✓'],
                  ['Custom Integrations', '—', '—', 'On request', '✓'],
                  ['Custom Branding', '—', '✓', '✓', '✓'],
                  ['HIPAA BAA', '—', '✓', '✓', '✓'],
                  ['Support Response', '48hr', '24hr', '4hr', 'SLA-based'],
                  ['Dedicated Account Mgr', '—', '—', '✓', '✓'],
                  ['On-site Training', '—', '—', '—', '✓'],
                  ['Uptime SLA', '99%', '99.5%', '99.9%', '99.99%'],
                ].map(([feature, ...vals], ri) => (
                  <tr key={ri} style={{ borderBottom: `1px solid ${c.cardBorder}` }}>
                    <td style={{ padding: '12px 16px', color: c.textPrimary, fontWeight: 500 }}>{feature}</td>
                    {vals.map((v, ci) => (
                      <td key={ci} style={{ padding: '12px 16px', textAlign: 'center', color: v === '✓' ? '#00d4aa' : v === '—' ? c.textMuted : c.textSecondary, fontWeight: v === '✓' ? 700 : 400 }}>
                        {v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </section>

      {/* ─── FAQ ─── */}
      <section style={{ background: c.pageBg2, padding: '80px 24px' }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <h2 style={{ fontSize: 36, fontFamily: 'DM Serif Display, serif', marginBottom: 12, color: c.textPrimary }}>Pricing FAQ</h2>
            </div>
          </Reveal>
          {faqs.map((faq, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <FAQItem q={faq.q} a={faq.a} borderColor={c.cardBorder} textColor={c.textPrimary} mutedColor={c.textSecondary} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* ─── CTA SECTION ─── */}
      <section style={{ padding: '80px 24px', textAlign: 'center' }}>
        <Reveal>
          <div style={{ maxWidth: 600, margin: '0 auto' }}>
            <h2 style={{ fontSize: 36, fontFamily: 'DM Serif Display, serif', marginBottom: 16, color: c.textPrimary }}>Ready to modernize your surveillance?</h2>
            <p style={{ fontSize: 16, color: c.textSecondary, lineHeight: 1.7, marginBottom: 32 }}>
              Join health departments worldwide using EpiSurveillance to protect their communities. Start in minutes — no hardware, no IT infrastructure needed.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
              <Link to="/register" className="p-cta-primary" style={{ padding: '16px 40px', fontSize: 17 }}>Get Started Today</Link>
              <a href="mailto:support@episurveillance.com?subject=EpiSurveillance%20Demo%20Request" className="p-cta-secondary" style={{ padding: '16px 40px', fontSize: 17 }}>Schedule a Demo</a>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ─── FOOTER ─── */}
      <footer style={{ background: isDark ? '#020617' : '#0f172a', padding: '64px 24px 32px', color: '#94a3b8' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 40, marginBottom: 48 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #00d4aa, #00b894)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 12, color: '#020617' }}>ES</div>
                <span style={{ fontSize: 16, fontWeight: 800, color: '#f1f5f9', fontFamily: 'DM Serif Display, serif' }}>EpiSurveillance</span>
              </div>
              <p style={{ fontSize: 14, lineHeight: 1.6, color: '#64748b' }}>The Modern Era of Disease Surveillance.</p>
              <div style={{ fontSize: 13, color: '#475569', marginTop: 12 }}>2003 Plank Road #1283<br />Fredericksburg, VA 22401</div>
            </div>
            <div>
              <h4 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#64748b', marginBottom: 20 }}>Platform</h4>
              {[['Features', '/#features'], ['Pricing', '/pricing'], ['Modules', '/#modules'], ['AI Insights', '/platform/ai-insights']].map(([l, p]) => (
                <div key={l} style={{ marginBottom: 12 }}><Link to={p} style={{ fontSize: 14, color: '#94a3b8', textDecoration: 'none' }}>{l}</Link></div>
              ))}
            </div>
            <div>
              <h4 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#64748b', marginBottom: 20 }}>Company</h4>
              {[['About', '/company/about'], ['Careers', '/company/careers'], ['Security', '/company/security'], ['Compliance', '/company/compliance']].map(([l, p]) => (
                <div key={l} style={{ marginBottom: 12 }}><Link to={p} style={{ fontSize: 14, color: '#94a3b8', textDecoration: 'none' }}>{l}</Link></div>
              ))}
            </div>
            <div>
              <h4 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', color: '#64748b', marginBottom: 20 }}>Contact</h4>
              <div style={{ marginBottom: 10 }}><a href="mailto:support@episurveillance.com" style={{ fontSize: 14, color: '#94a3b8', textDecoration: 'none' }}>support@episurveillance.com</a></div>
              <div style={{ marginBottom: 10 }}><a href="tel:5403227406" style={{ fontSize: 14, color: '#94a3b8', textDecoration: 'none' }}>(540) 322-7406</a></div>
            </div>
          </div>
          <div style={{ borderTop: '1px solid #1e293b', paddingTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ fontSize: 13, color: '#475569' }}>© {new Date().getFullYear()} EpiSurveillance. All rights reserved.</div>
            <div style={{ display: 'flex', gap: 24, fontSize: 13 }}>
              {[['Privacy', '/legal/privacy'], ['Terms', '/legal/terms'], ['Cookies', '/legal/cookies'], ['HIPAA', '/company/compliance']].map(([l, p]) => (
                <Link key={l} to={p} style={{ color: '#64748b', textDecoration: 'none' }}>{l}</Link>
              ))}
              <button onClick={openCookieSettings} style={{ background: 'none', border: 'none', padding: 0, color: '#64748b', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Cookie settings</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;